/**
 * @file builder-context.ts
 * @description Контекст конструктора сайта (phase19.16 Р1-6а;
 * uiux-builder-shell.md §2, §5): выбор в адресе, страницы и блоки, один
 * iframe канвы на все панели, источник канвы («Посмотреть» дизайн или
 * версию), защита несохранённого блока. Вид сайта (черновик, «что
 * изменено», права по частям, предпросмотр) — в `SiteViewContext` каркаса.
 *
 * phase19.16 Р2: «Отменить / Вернуть» (`undo`), выбранный элемент секции
 * (`element`: канва и панель выбирают один и тот же), библиотека заготовок
 * с местом вставки и окно «Сделать главной».
 *
 * phase19.16 Р2а: что показывает канва каталога и шаблона товара
 * (`canvasTarget`: категория «Показать: …» и товар для примера).
 *
 * phase19.16 Р3а: черновик и публикация открытой страницы (`publishing`),
 * панель «Публикации» (на ПК — группа в «Настройках страницы», до 1280 —
 * шторка), «Все версии» и «Удалённые страницы»; «Посмотреть» версию
 * страницы — `CanvasOverride` с `pageVersion` (вход витрины с pageId и version).
 */

import type { SectionPresetGroup } from "@chaiberi/shared";
import { createContext, useContext, type MutableRefObject, type RefObject } from "react";

import type { BlockActions, FormSink } from "../../hooks/site-builder/useBlockActions";
import type { BuilderPagesApi } from "../../hooks/site-builder/useBuilderPages";
import type { DesignsApi } from "../../hooks/site-builder/useDesigns";
import type { PagePublishingApi } from "../../hooks/site-builder/usePagePublishing";
import type { UndoApi } from "../../hooks/site-builder/useUndoStack";
import type { TemplateSampleProduct } from "../../lib/api-site-templates";
import type { BuilderSelection } from "../../lib/site-builder/selection";
import type { SitePreviewSource } from "../../lib/site-view/types";
import type { ConfirmRequest } from "../ui/use-confirm";

/** Канва показывает не черновик, а дизайн или прошлую версию. */
export interface CanvasOverride {
  /** Ключ предпросмотра; версия страницы (Р3а) — ключ черновика. */
  source: SitePreviewSource;
  /** Р3а: версия страницы на канве (`pageRevisionFrameUrl`); подпись версии рисует витрина. */
  pageVersion?: { pageId: string; version: number };
  /** «Предпросмотр дизайна «Лето»», «Версия от 05.10 18:02». */
  label: string;
  /** Действие полосы: «Применить ко всему сайту», «Вернуть эту версию». */
  action?: { label: string; run: () => void };
}

/** Раскладка по ширине окна (§2, §9 проекта): телефон, планшет, компьютер. */
export type BuilderLayout = "phone" | "tablet" | "desktop";

export interface BuilderContextValue {
  selection: BuilderSelection;
  /** Сменить выбор; `extra` — служебные параметры адреса (`font`, `back`), см. useBuilderSelection. */
  select: (patch: Partial<BuilderSelection>, extra?: Readonly<Record<string, string>>) => Promise<boolean>;
  setGuard: (guard: (() => Promise<boolean>) | null) => void;
  /** Спросить «Блок не сохранён» без смены выбора (уход, который адрес не меняет). */
  leave: () => Promise<boolean>;
  pages: BuilderPagesApi;
  blocks: BlockActions;
  iframeRef: RefObject<HTMLIFrameElement | null>;
  override: CanvasOverride | null;
  setOverride: (override: CanvasOverride | null) => void;
  confirm: (request: ConfirmRequest) => Promise<boolean>;
  layout: BuilderLayout;
  /** Может править страницы (право контента). */
  canEditPages: boolean;
  canPublishPages: boolean;
  /** Может менять вид сайта целиком: оформление, дизайны (право настроек). */
  canEditSettings: boolean;
  /** Библиотека дизайнов (одна на панель и окно «Сохранить как дизайн»). */
  designs: DesignsApi;
  /** Открыть окно «Сохранить текущий вид как дизайн». */
  openSaveDesign: () => void;
  /** Открыть окно «Новая страница». */
  openAddPage: () => void;
  /** «Весь сайт ↗»: предпросмотр на весь экран того, что на канве (и дизайна или версии). */
  openWholeSite: () => void;
  /** Устройство канвы уже выбрано (по ширине колонки или человеком) — не менять. */
  deviceChosen: MutableRefObject<boolean>;
  /** «Отменить / Вернуть» действий с блоками открытой страницы. */
  undo: UndoApi;
  /** Выбранный элемент секции (`null` — открыта сама секция). */
  element: ElementSel | null;
  setElement: (element: ElementSel | null) => void;
  /**
   * Открыть «Добавить блок»: `after` — после какого блока (`null` — в начало
   * страницы, `undefined` — после выбранного, иначе в конец).
   */
  openLibrary: (after?: string | null, options?: LibraryOptions) => void;
  /** Открыть «Сделать главной» для страницы. */
  openSetHome: (pageId: string) => void;
  /** Р3а: черновик и публикация открытой страницы. */
  publishing: PagePublishingApi;
  /** Р3а: панель «Публикации» (ПК — группа настроек страницы, иначе шторка). */
  openPublications: () => void;
  /** Р3а: «Все версии» открытой страницы. */
  openVersions: () => void;
  /** Р3а: окно публикации, где открытая страница отмечена с «Показывать на сайте» (SB128). */
  openPublishToShow: () => void;
  /** Р3а: «Удалённые страницы» с «Восстановить». */
  openDeletedPages: () => void;
  /** Р3а: «Посмотреть» версию страницы на канве (плашка «Версия N — так было на сайте»). */
  viewPageVersion: (version: { version: number; publishedAt: string; isLive: boolean }) => void;
  /** Блок, которому сразу предложить фото фона (заготовка с фото-фоном). */
  bgPhotoRequest: string | null;
  /** Форма блока, открытая в панели: правки текста с канвы при несохранённой форме идут в неё. */
  formSink: MutableRefObject<FormSink | null>;
  setBgPhotoRequest: (blockId: string | null) => void;
  /** Р2а: категория и товар для примера на канве. */
  canvasTarget: CanvasTarget;
  setCanvasTarget: (target: CanvasTarget) => void;
}

/** Что показать на канве: категория каталога и товар для примера шаблона; `null` — по умолчанию. */
export interface CanvasTarget {
  /** Адрес категории (`/catalog/<slug>`); `null` — весь каталог. */
  category: string | null;
  /** Товар для примера; `null` — пример сервера или первый товар. */
  product: TemplateSampleProduct | null;
}

/** Окно «Добавить блок»: какую группу заготовок показать первой. */
export interface LibraryOptions {
  group?: SectionPresetGroup;
}

/** Элемент секции: блок и id элемента внутри него. */
export interface ElementSel {
  blockId: string;
  elementId: string;
}

export const BuilderContext = createContext<BuilderContextValue | null>(null);

export function useBuilder(): BuilderContextValue {
  const ctx = useContext(BuilderContext);
  if (!ctx) throw new Error("useBuilder(): компонент конструктора должен быть внутри SiteBuilderPage");
  return ctx;
}
